import React from "react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import { useTheme } from "@mui/material/styles";
import type { BoxProps } from "@mui/material/Box";
import { keyframes } from "@emotion/react";
import type { BaseMoleculeProps } from "../../types";

// Wave animation keyframes
const waveAnimation = keyframes`
  0%, 40%, 100% {
    transform: scaleY(0.4);
    opacity: 0.5;
  }
  20% {
    transform: scaleY(1);
    opacity: 1;
  }
`;

export interface LoadingAnimationProps extends BaseMoleculeProps {
  /**
   * Size of the loading animation
   */
  size?: "small" | "medium" | "large";

  /**
   * Color theme for the animation
   */
  color?: "primary" | "secondary";

  /**
   * Whether to show as backdrop overlay
   */
  backdrop?: boolean;

  /**
   * Additional Box props for container styling
   */
  containerProps?: BoxProps;
}

const LoadingAnimationComponent: React.FC<LoadingAnimationProps> = ({
  size = "large",
  color = "primary",
  backdrop = false,
  loading = true,
  variant = "default",
  className,
  "data-testid": dataTestId,
  containerProps,
  children,
}) => {
  const theme = useTheme();

  // Don't render if not loading
  if (!loading) {
    return <>{children}</>;
  }

  // Size configurations
  const sizeConfig = {
    small: {
      barWidth: 4,
      barHeight: 20,
      gap: 3,
      containerHeight: 30,
    },
    medium: {
      barWidth: 5,
      barHeight: 32,
      gap: 4,
      containerHeight: 40,
    },
    large: {
      barWidth: 6,
      barHeight: 48,
      gap: 5,
      containerHeight: 60,
    },
  };

  const config = sizeConfig[size];
  const colorValue =
    color === "primary"
      ? theme.palette.primary.main
      : theme.palette.secondary.main;

  // Create 5 bars for wave effect
  const bars = Array.from({ length: 5 }, (_, i) => i);

  const loadingContent = (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight={backdrop ? "100vh" : variant === "modal" ? "300px" : "200px"}
      padding={2}
      className={className}
      data-testid={dataTestId}
      sx={{
        gap: {
          xs: 2,
          md: 3,
        },
        ...(variant === "compact" && {
          minHeight: "100px",
          padding: 1,
        }),
        ...(variant === "expanded" && {
          minHeight: backdrop ? "100vh" : "400px",
          padding: 4,
        }),
        ...(variant === "modal" && {
          minHeight: "300px",
          maxHeight: "60vh",
          padding: { xs: 3, md: 4 },
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }),
      }}
      {...containerProps}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={config.gap}
        height={config.containerHeight}
        sx={{
          // Mobile-first responsive sizing
          gap: {
            xs: config.gap * 0.75,
            md: config.gap,
          },
        }}
      >
        {bars.map((index) => (
          <Box
            key={index}
            sx={{
              width: {
                xs: config.barWidth * 0.8,
                md: config.barWidth,
              },
              height: {
                xs: config.barHeight * 0.8,
                md: config.barHeight,
              },
              backgroundColor: colorValue,
              borderRadius: "2px",
              animation: `${waveAnimation} 1.2s ease-in-out infinite`,
              animationDelay: `${index * 0.1}s`,
              transformOrigin: "center",
            }}
            aria-hidden="true"
          />
        ))}
      </Box>
    </Box>
  );

  // Render with backdrop if requested
  if (backdrop) {
    return (
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "background.default",
        }}
      >
        {loadingContent}
      </Backdrop>
    );
  }

  return loadingContent;
};

// Apply memoization with React.memo
export const LoadingAnimation = React.memo(LoadingAnimationComponent);

// Set display name for debugging
(LoadingAnimation as any).displayName = "LoadingAnimation";
